/**
 * Typed GraphQL reads for a place's members (place manager, unit 3).
 * Read-only: nothing in this module writes.
 *
 * A place has no roster of its own on the backend. Its members, today, are the
 * people who follow one of its seasons (`Season.followers`); there is no
 * leaving and no visits to read. Every scorer in a season's events is made a
 * follower automatically, so a season's list grows with play. The place's
 * seasons come from unit 2's read (`PlaceSeasonsQuery` in
 * `place-events-api.ts`), read once per visit and shared.
 *
 * `Season.followers` takes no arguments: it answers every follower of the
 * season, with `totalCount`, unordered and unpaged. So the page reads in two
 * steps: the count alone per season (`SeasonFollowerCountQuery`, for the
 * People card and the section's summary), and the whole list
 * (`SeasonFollowersQuery`) only when the partner opens the members, once per
 * visit. The count alone keeps the answer small; the backend's resolver still
 * loads every follower row (and its user) to answer it, until followers take
 * paging or a place roster exists (an ask on the lane's later list).
 * Its `nodes` are the users themselves (`edges` wraps each in `{ user }`).
 * One read per season, side by side, so one season not reading leaves the
 * others standing.
 */

import { graphql, gqlRequestAs, type RequestOptions, type ResultOf } from '@/lib/graphql';
import type { RequestViewer } from '@/lib/request-headers';

/** Everyone who follows a season: each one's id (to count people once across
 *  seasons) and face. */
export const SeasonFollowersFields = graphql(`
  fragment SeasonFollowersFields on Season {
    id
    followers {
      totalCount
      nodes {
        id
        public {
          displayName
          username
          photoURL
        }
      }
    }
  }
`);

export const SeasonFollowersQuery = graphql(
  `
  query SeasonFollowers($id: ID!) {
    season(id: $id) {
      ...SeasonFollowersFields
    }
  }
`,
  [SeasonFollowersFields],
);

/** How many follow a season: the count alone, no list. */
export const SeasonFollowerCountQuery = graphql(`
  query SeasonFollowerCount($id: ID!) {
    season(id: $id) {
      id
      followers {
        totalCount
      }
    }
  }
`);

/** How many follow a season. Null when the season does not exist or is
 *  archived (to anyone but staff): not readable by this viewer. */
export async function fetchSeasonFollowerCount(
  seasonId: string,
  viewer: RequestViewer,
  options?: RequestOptions,
): Promise<number | null> {
  const data = await gqlRequestAs(viewer, SeasonFollowerCountQuery, { id: seasonId }, options);
  return data.season?.followers.totalCount ?? null;
}

/** A season's followers, as the read returns them. */
export type SeasonFollowersData = ResultOf<typeof SeasonFollowersFields>['followers'];

/** A season's followers. Null when the season does not exist or is archived
 *  (to anyone but staff): not readable by this viewer. */
export async function fetchSeasonFollowers(
  seasonId: string,
  viewer: RequestViewer,
  options?: RequestOptions,
): Promise<SeasonFollowersData | null> {
  const data = await gqlRequestAs(viewer, SeasonFollowersQuery, { id: seasonId }, options);
  return data.season?.followers ?? null;
}
